import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { IDetailPageState } from '../../reducers'

import {
  GenericDetailsPageClient,
  GenericDetailsPageClientProps,
  CallbackArgs
} from 'DVPAWebWidgetsDetailsPageClient'

let PurchaseFlowBootloader: typeof GenericDetailsPageClient
if (!global.__SERVER__ || global.__DEVSERVER__) {
  PurchaseFlowBootloader = require('DVPAWebWidgetsDetailsPageClient')
    .GenericDetailsPageClient
}

import {
  purchaseSelector,
  offerTokenToPurchaseActionSelector
} from '../../selectors/purchase'
import { subPageTypeSelector } from '../../selectors/detailFragment'
import { DetailPageActions } from '../../actions'
import { Dispatch } from 'redux'
import {
  purchaseCancelled,
  purchaseFailed,
  refreshAfterDone,
  triggerPurchase,
  refreshCSRF
} from '../../actions/purchase'
import { withLogger, ILogger } from '../../context/withLogger'

import { COUNTER_PACKAGE_PREFIX } from '../../utils/utils'
import { ErrorBoundary } from '../error/ErrorBoundary'
import {
  refundStatusCallback,
  RefundStatus,
  refundCancelled,
  refundFailed
} from '../../actions/refund'
import { Logger, replaceState } from '@utils'
import { refundingDetails } from '../../selectors/refundFragment'
import { getDeviceInfo } from '@utils'

type OwnProps = ILogger
export type IPurchaseFlowBootloader = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps

type State = {
  initialized: boolean
}

const RESTART_PURCHASE_DELAY = 300 // To allow time for player capabilities to be retrieved
const PURCHASE_ELEMENT_ID = 'mfa-purchase-root'

const getQueryParam = (param: string) => {
  const params = window.location.search
  const result = (params || '').match(`${param}=([^&]+)`)
  return result && decodeURIComponent(result[1])
}

export const stripTokens = (url: string) =>
  url
    .replace(/continueToken=[^&]+&?/, '')
    .replace(/offerToken=[^&]+&?/, '')
    .replace(/gti=[^&]+&?/, '')

export class ConnectedPurchaseFlowWrapper extends React.Component<
  IPurchaseFlowBootloader
> {
  private el: HTMLDivElement | undefined
  private portalRoot: HTMLElement | null
  state: State

  constructor(props: IPurchaseFlowBootloader) {
    super(props)
    this.state = {
      initialized: false
    }
    this.portalRoot = null
  }

  componentDidMount() {
    const { triggerPurchase, offerTokenToPurchaseAction } = this.props
    if (this.shouldInitialize()) {
      this.initialize()

      const continueToken = getQueryParam('continueToken')
      const offerToken = getQueryParam('offerToken')
      const gtiRedirect = getQueryParam('gti')

      //To continue purchase, we need either an offerToken match (which will not be there in ROW because of OfferTokenV2), or a GTI
      if (
        continueToken &&
        offerToken &&
        (offerTokenToPurchaseAction[offerToken] || gtiRedirect)
      ) {
        setTimeout(
          () => triggerPurchase(offerToken, continueToken, gtiRedirect),
          RESTART_PURCHASE_DELAY
        )

        if (window.location.href) {
          replaceState(null, document.title, stripTokens(window.location.href))
        }
      }
    }
  }

  componentDidUpdate() {
    if (!this.state.initialized && this.shouldInitialize()) {
      this.initialize()
    }
  }

  componentWillUnmount() {
    if (this.portalRoot && this.el) {
      this.portalRoot.removeChild(this.el)
    }
  }

  getPurchaseElement() {
    let el = document.getElementById(PURCHASE_ELEMENT_ID)

    if (el) {
      return el
    }

    el = document.createElement('div')
    el.setAttribute('id', PURCHASE_ELEMENT_ID)
    document.body.appendChild(el)
    return el
  }

  initialize() {
    this.portalRoot = this.getPurchaseElement()
    this.el = document.createElement('div')
    this.portalRoot.appendChild(this.el)
    this.setState({ initialized: true })
  }

  shouldInitialize = () =>
    (Object.keys(this.props.offerTokenToPurchaseAction).length > 0) || this.props.hasRefunds

  render() {
    const {
      purchasingDetails,
      refundingDetails,
      callbackCancel,
      resetAfterCancel,
      callbackFailure,
      failed,
      refreshAfterDone,
      callbackCSRF,
      callbackCancelRefund,
      callbackFailureRefund,
      callbackRefund,
      subPageType,
      titleId,
      capabilities,
      offerTokenToPurchaseAction,
      deviceId
    } = this.props

    const { initialized } = this.state

    const playerDetails = {
      supportsWebPlayback: Boolean(capabilities) && capabilities.supportsWebPlayback,
      supportsHDMovies: Boolean(capabilities) && capabilities.supportsHDMovies,
      supportsUHD: false
    }

    if (!initialized) {
      return null
    }

    let props: GenericDetailsPageClientProps = {
      action: undefined,
      actionContext: {
        callbacks: {
          callbackCancel: () => {},
          callbackFailure: () => {}
        }
      }
    }

    if (purchasingDetails) {
      const { details, continueToken, refTag, gtiRedirect } = purchasingDetails

      // In ROW on continue purchase flow, "details" will be undefined because the offerToken in the URL
      // will not match any of the offer token from TAPS (OfferToken V2). Passing blindly the offer token
      // From the URL to workflow
      const offerToken =
        (details && details.offer.offerToken) || purchasingDetails.offerToken

      let csrfToken = details && details.offer.csrfTokenWorkflow
      if (!csrfToken) {
        // In ROW, on continue purchase flow, the value we submit to workflow for csrfToken
        // is taken from any offer (best effort)
        const anyAction = Object.values(offerTokenToPurchaseAction)[0]
        csrfToken = anyAction && anyAction.offer.csrfTokenWorkflow
      }

      //In ROW, on continue purchase flow, the GTI is given in the query params
      const globalTitleIdentifier = (details && details.gti) || gtiRedirect
      const { deviceTypeId } = getDeviceInfo();

      props = {
        action: 'purchase',
        actionContext: {
          isVisible: true,
          playerDetails,
          extraData: {
            subPageType,
            titleId,
            refTag,
            globalTitleIdentifier,
            ...({ deviceTypeId }),
            ...(deviceId && { deviceId })
          },
          callbacks: {
            callbackCancel,
            callbackFailure,
            resetAfterCancel,
            refreshAfterDone,
            failed,
            callbackCSRF
          },
          offerContext: { offerToken, csrfToken },
          continueToken
        }
      }
    } else if (refundingDetails) {
      props = {
        action: 'refund',
        actionContext: {
          isVisible: true,
          asin: refundingDetails.asin,
          csrfToken: refundingDetails.csrfToken,
          globalTitleIdentifier: refundingDetails.gti,
          callbacks: {
            callbackCancel: callbackCancelRefund,
            callbackFailure: callbackFailureRefund,
            callbackRefundSuccess: callbackRefund('Success'),
            callbackRefundFailure: callbackRefund('Failure')
          }
        }
      }
    }
    return ReactDOM.createPortal(
      <ErrorBoundary name='purchaseWorkflow'>
        <PurchaseFlowBootloader {...props} />
      </ErrorBoundary>,
      this.el!
    )
  }
}

const mapStateToProps = (state: IDetailPageState) => ({
  purchasingDetails: purchaseSelector(state),
  refundingDetails: refundingDetails(state),
  offerTokenToPurchaseAction: offerTokenToPurchaseActionSelector(state),
  hasRefunds: Object.keys(state.refund.fragments).length > 0,
  subPageType: subPageTypeSelector(state),
  deviceId: state.requestContext.deviceID,
  titleId: state.pageTitleId,
  capabilities: state.player.capabilities
})

export interface CallbackInput {
  dispatch: Dispatch<DetailPageActions>
  logger: Logger
}

type callbackType = (props: CallbackInput) => unknown

const mapDispatchToProps = (
  dispatch: Dispatch<DetailPageActions>,
  { logger }: OwnProps
) => {
  const logCallback = (action: 'Purchase' | 'Refund') => (
    callback: callbackType,
    callbackName: string,
    isError: boolean = false
  ) => {
    const log = isError ? logger.error : logger.info
    log(`[${action}] - Callback ${callbackName} triggered`).count(
      `${COUNTER_PACKAGE_PREFIX}_${action}_${callbackName}`,
      1,
      true
    )
    callback({ dispatch, logger })
  }

  const logPurchase = logCallback('Purchase')
  const logRefund = logCallback('Refund')

  return {
    //Purchase Callbacks
    callbackCancel: () => logPurchase(purchaseCancelled, 'callbackCancel'),
    resetAfterCancel: () => logPurchase(purchaseCancelled, 'resetAfterCancel'),
    callbackFailure: () => logPurchase(purchaseFailed, 'callbackFailure', true),
    failed: () => logPurchase(purchaseFailed, 'failed', true),
    refreshAfterDone: () => logPurchase(refreshAfterDone, 'refreshAfterDone'),
    callbackCSRF: () => logPurchase(refreshCSRF, 'callbackCSRF'),

    //Refund Callbacks
    callbackCancelRefund: () => logRefund(refundCancelled, 'callbackCancel'),
    callbackFailureRefund: () => logRefund(refundFailed, 'callbackFailure'),
    callbackRefund: (status: RefundStatus) => (args: CallbackArgs) =>
      logRefund(refundStatusCallback(status, args), `callbackRefund${status}`),
    triggerPurchase: (
      offerToken: string,
      continueToken: string,
      gtiRedirect?: string
    ) =>
      triggerPurchase(
        dispatch,
        logger,
        offerToken,
        continueToken,
        undefined,
        gtiRedirect
      ),
  }
}

export const PurchaseFlowWrapper = withLogger(
  connect(mapStateToProps, mapDispatchToProps)(ConnectedPurchaseFlowWrapper)
)
